import { chapterLoaders } from './data/index.js';
import type { ChapterData, ContentProvider } from './systems/adventures/types.js';

/**
 * Chapter cache over the generated lazy loaders. The engine reads it
 * synchronously; callers `ensure()` a chapter before dispatching an action
 * that needs it.
 */
export class ChapterCache implements ContentProvider {
  private readonly loaded = new Map<number, ChapterData>();
  private readonly pending = new Map<number, Promise<ChapterData>>();

  constructor(private readonly loaders: readonly (() => Promise<ChapterData>)[] = chapterLoaders) {}

  getChapter(index: number): ChapterData | undefined {
    return this.loaded.get(index);
  }

  get count(): number {
    return this.loaders.length;
  }

  ensure(index: number): Promise<ChapterData> {
    const hit = this.loaded.get(index);
    if (hit) return Promise.resolve(hit);
    const inflight = this.pending.get(index);
    if (inflight) return inflight;
    const loader = this.loaders[index];
    if (!loader) return Promise.reject(new Error(`no chapter ${index}`));
    const p = loader().then(
      (data) => {
        this.loaded.set(index, data);
        this.pending.delete(index);
        return data;
      },
      (err: unknown) => {
        // A failed chunk (flaky network) must not stay cached: the next call retries.
        this.pending.delete(index);
        throw err;
      },
    );
    this.pending.set(index, p);
    return p;
  }

  async ensureAll(): Promise<void> {
    await Promise.all(this.loaders.map((_, i) => this.ensure(i)));
  }
}
